import React, { FC, useCallback, useEffect, useState } from 'react';
import './Lightbox.css';
import { IoArrowBack, IoArrowForward, IoClose } from '@/Shared/Lib/UI/Icons';
import FrameView from '../FrameView/FrameView';
import { LightBoxEvent } from '@/Shared/Logic/Hooks/TechnicalHooks/useManageLightBox';
import LightBoxLoader from '../ImageLoader/ImageLoader';
import { LightBoxData, useMedia } from '@/Shared/Logic';


interface LightboxProps {
	data: LightBoxData | null;
	onClose: () => void;
}

/**
 * =====================================================
 * Component: LightBox
 * =====================================================
 *
 * Description:
 * Full-screen media viewer component for images and videos.
 * Controlled via an external hook (`useLightBox`) following the command pattern.
 *
 * Responsibilities:
 * - Display the current media item with optional caption.
 * - Handle navigation between media items (prev/next).
 * - Provide zoom, drag, and wheel interactions for images.
 * - Display loader while images are loading.
 * - Listen for keyboard events for navigation and closing.
 *
 * Notes:
 * - Stateless with respect to media data; driven entirely by `LightBoxData` prop.
 * - Integrates `useMedia` hook for interactive image behavior.
 *
 * Author: Samano CASTRE
 * Date: February 4, 2026
 */
const LightBox: FC<LightboxProps> = ({ data, onClose }) => {


	/** =====================================================
     * Local UI State
     * =====================================================
     *
     * Tracks loading state for current media item
     * and the currently displayed index.
     */
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const { medias, initialIndex } = data ?? {};
	const media = useMedia();

	const [mediaItemIndex, setMediaItemIndex] = useState<number | undefined>(initialIndex);



	/** =====================================================
     * Navigation Handlers
     * =====================================================
     *
     * Functions to move to previous or next media item.
     * Wrap around at the start/end of the media array.
     */
	const onPrev = useCallback(() => {
		if (!medias) return;
		setMediaItemIndex(index => (index === 0 ? medias.length - 1 : (index ?? 0) - 1));
	}, [medias?.length]);

	const onNext = useCallback(() => {
		if (!medias) return;
		setMediaItemIndex(index => (index === medias.length - 1 ? 0 : (index ?? 0) + 1));
	}, [medias?.length]);

	useEffect(() => {
		setMediaItemIndex(initialIndex);
	},[initialIndex]);


	/** =====================================================
     * Keyboard Event Listener
     * =====================================================
     *
     * Listens for Escape, ArrowLeft, ArrowRight keys
     * to close or navigate the LightBox.
     */
	useEffect(() => {
		const handleKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') callAction(event, onClose);
			if (event.key === 'ArrowLeft') callAction(event, onPrev);
			if (event.key === 'ArrowRight') callAction(event, onNext);
		};
		window.addEventListener('keydown', handleKey);
		return () => window.removeEventListener('keydown', handleKey);
	}, [mediaItemIndex, onClose, onPrev, onNext]);


	/** =====================================================
     * Reset Loading State
     * =====================================================
     *
     * Resets `isLoading` whenever the current media index changes,
     * triggering the loader until the new media item finishes loading.
     */
	useEffect(() => {
		setIsLoading(true);
	}, [mediaItemIndex]);


	/** =====================================================
     * Helper Functions
     * =====================================================
     *
     * callAction: prevent default/stop propagation for UI events
     */
	const callAction = (event: LightBoxEvent, action: () => void) => {
		event.preventDefault();
		event.stopPropagation();
		action();
	}

	/** =====================================================
     * Guard Clause
     * =====================================================
     *
     * Return null if no media is available to display.
     */
	if (!medias) {
		return null;
	}


	const currentItem = medias[mediaItemIndex ?? 0];


	/** =====================================================
     * Render
     * =====================================================
     *
     * Overlay container, media content, navigation buttons,
     * caption, and loader.
     */
	return (
		<div className="LightBox custom-lightbox-overlay" data-testid="LightBox">
			<div className="custom-lightbox-content"

				onMouseMove={media.handleMouseMove}
				onMouseUp={media.handleMouseUp}
				onMouseLeave={media.handleMouseUp}

			>
				{currentItem.caption && <div className="LightBox__title">{currentItem.caption}</div>}

				<button type='button' className="custom-lightbox-button custom-lightbox-close" onClick={onClose}><IoClose /></button>
				<button type='button' className="custom-lightbox-button custom-lightbox-prev" onClick={onPrev}><IoArrowBack /></button>
				<button type='button' className="custom-lightbox-button custom-lightbox-next" onClick={onNext}><IoArrowForward /></button>

				{
					currentItem.type === 'image' ? (
						<div className='custom-lightbox-media-wrapper'>
							{isLoading && <LightBoxLoader key={"lightbox-loader-" + currentItem.id} />}
							
							<img
								key={currentItem.id}
								ref={media.mediaRef}
								src={currentItem.large || currentItem.small}
								alt={currentItem.caption ?? ''}
								className={`custom-lightbox-media ${media.isZoomed ? 'zoomed' : ''}`}

								onLoad={() => setIsLoading(false)}
								onError={() => setIsLoading(false)}

								onWheel={media.onWheel}
								onMouseDown={media.handleMouseDown}
								onDoubleClick={media.toggleZoom}
								style={{
									cursor: media.isZoomed ? 'grab' : 'zoom-in',
									transform: media.isZoomed
									? `translate(${media.position.x}px, ${media.position.y}px) scale(${media.scale})`
									: 'scale(1)',
									transition: media.isDragging ? 'none' : 'transform 0.2s ease',
									userSelect: 'none',
									pointerEvents: 'auto'
								}}
								draggable={false}
							/>
						</div>
					) : (<FrameView url={currentItem.large || currentItem.small || ""} />)
				}
			</div>
		</div>
	);
};

export default LightBox;